import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useTransition,
} from "react";

import { Alert } from "@stacklet/ui";
import { FormProvider, useForm } from "react-hook-form";
import { useFragment, useRefetchableFragment } from "react-relay";
import { graphql } from "relay-runtime";

import { ACLPrincipalPicker, type ACLTarget } from "./ACLPrincipalPicker";

import type {
  ACLFormFields_acl$key,
  ACLFormFields_acl$data,
} from "./__generated__/ACLFormFields_acl.graphql";
import type { ACLFormFields_query$key } from "./__generated__/ACLFormFields_query.graphql";

type ACLEdge = NonNullable<
  NonNullable<ACLFormFields_acl$data["acl"]>["edges"]
>[number];

interface RoleGroup {
  roleName: string;
  principals: {
    id: string;
    type: "User" | "UserGroup";
    displayName: string;
    name?: string;
    key?: string;
    email?: string;
  }[];
}

interface PrincipalOption {
  label: string;
  value: string;
  group: string;
  type: "User" | "UserGroup";
}

interface RoleAssignmentChange {
  roleName: string;
  principal: string;
  target: string;
}

export interface RoleChanges {
  grants: RoleAssignmentChange[];
  revokes: RoleAssignmentChange[];
}

type RoleGroupFormValue = Record<string, PrincipalOption[]>;

export type Role =
  | "creator"
  | "owner"
  | "editor"
  | "resource-viewer"
  | "policy-deployer";

interface Props {
  /** Query reference for root query data */
  queryRef: ACLFormFields_query$key;
  /** Callback for when role changes are made */
  onRoleChanges?: (changes: RoleChanges) => void;
  /** Current role changes to be applied */
  currentRoleChanges: RoleChanges;
  /** List of roles specific the form */
  availableRoles: Role[];
  /** Optional query reference if in edit mode for ACL data on an object */
  aclRef?: ACLFormFields_acl$key | null;
  /** Optional target for the input to display specific helper text */
  target?: ACLTarget;
  /** Whether the form is in edit mode */
  isEditMode?: boolean;
  disabled?: boolean;
}

export function ACLFormFields({
  aclRef,
  queryRef,
  onRoleChanges,
  currentRoleChanges,
  availableRoles,
  target,
  disabled = false,
  isEditMode = true,
}: Props) {
  const [error, setError] = useState<string | null>(null);
  const [, startTransition] = useTransition();

  const queryData = useFragment(
    graphql`
      fragment ACLFormFields_query on Query {
        ...ACLPrincipalPicker_principals
        whoami {
          email
          name
          roleAssignmentPrincipal
        }
      }
    `,
    queryRef,
  );

  const [data, refetch] = useRefetchableFragment(
    graphql`
      fragment ACLFormFields_acl on Node
      @refetchable(queryName: "ACLFormFieldsRefetchQuery") {
        ... on HasACL {
          roleAssignmentTarget
          acl(first: 1000) {
            edges {
              node {
                id
                role {
                  name
                }
                principal {
                  __typename
                  ... on User {
                    id
                    displayName
                    key
                    email
                    roleAssignmentPrincipal
                  }
                  ... on UserGroup {
                    id
                    name
                    roleAssignmentPrincipal
                  }
                }
              }
            }
          }
        }
      }
    `,
    aclRef,
  );

  useEffect(() => {
    if (data) {
      startTransition(() => {
        refetch({}, { fetchPolicy: "network-only" });
      });
    }
  }, [refetch, data, startTransition]);

  const roleGroups = useMemo(() => {
    const groupsByRole: Record<string, RoleGroup> = {};

    ((data?.acl?.edges as readonly ACLEdge[]) || []).forEach(({ node }) => {
      const { role, principal } = node;

      if (!groupsByRole[role.name]) {
        groupsByRole[role.name] = {
          roleName: role.name,
          principals: [],
        };
      }

      // Handle the principal based on its __typename
      if (principal.__typename === "User") {
        groupsByRole[role.name].principals.push({
          id: principal.roleAssignmentPrincipal || principal.id,
          type: "User",
          displayName:
            principal.displayName ||
            principal.email ||
            principal.key?.toString() ||
            "",
          email: principal.email || undefined,
          key: principal.key?.toString(),
        });
      } else if (principal.__typename === "UserGroup") {
        groupsByRole[role.name].principals.push({
          id: principal.roleAssignmentPrincipal || principal.id,
          type: "UserGroup",
          displayName: principal.name || "",
          name: principal.name,
        });
      }
    });

    return Object.values(groupsByRole);
  }, [data?.acl?.edges]);

  // Create initial form values from role groups
  const initialFormValues = useMemo(() => {
    const initialValues: RoleGroupFormValue = {};

    // Initialize all roles from availableRoles prop
    availableRoles.forEach((roleName) => {
      initialValues[roleName] = [];
    });

    // Add any existing principals to their respective roles
    roleGroups.forEach((group) => {
      initialValues[group.roleName] = group.principals.map((principal) => ({
        label: principal.displayName,
        value: principal.id,
        group: principal.type === "UserGroup" ? "Groups" : "Users",
        type: principal.type,
      }));
    });

    // Pre-fill the "owner" role with the current user if it's one of the available roles
    if (!isEditMode && availableRoles.includes("owner") && queryData?.whoami) {
      const { email, name, roleAssignmentPrincipal } = queryData.whoami;
      const canPrefillOwner = !!roleAssignmentPrincipal && (!!email || !!name);

      if (canPrefillOwner) {
        const currentUser: PrincipalOption = {
          // Email or name is always truthy via above check. Current User will never be used.
          label: email || name || "Current User",
          value: roleAssignmentPrincipal,
          group: "Users",
          type: "User",
        };

        const existingOwners = initialValues["owner"] || [];
        const alreadyIncluded = existingOwners.some(
          (owner) => owner.value === currentUser.value,
        );

        if (!alreadyIncluded) {
          initialValues["owner"] = [...existingOwners, currentUser];
        }
      }
    }

    return initialValues;
  }, [roleGroups, availableRoles, isEditMode, queryData]);

  const methods = useForm<RoleGroupFormValue>({
    defaultValues: initialFormValues,
  });

  // Get all roles to display with display names
  const rolesToDisplay = useMemo(() => {
    // Transform role names from prop into the format needed for display
    return availableRoles.map((roleName) => ({
      id: roleName,
      displayName: roleName
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" "),
    }));
  }, [availableRoles]);

  const handleGrantAccess = useCallback(
    (roleName: string, newPrincipal: PrincipalOption) => {
      setError(null);

      if (isEditMode && !data?.roleAssignmentTarget) {
        setError("Cannot assign roles: No valid target found");
        return;
      }
      const target = !isEditMode ? "" : data?.roleAssignmentTarget;

      const prevGrants = currentRoleChanges.grants;
      const prevRevokes = currentRoleChanges.revokes;

      const newGrant: RoleAssignmentChange = {
        roleName,
        principal: newPrincipal.value,
        target: target,
      };

      const updatedRevokes = prevRevokes.filter(
        (revoke) =>
          !(
            revoke.roleName === roleName &&
            revoke.principal === newPrincipal.value &&
            revoke.target === target
          ),
      );

      const grantExists = prevGrants.some(
        (grant) =>
          grant.roleName === roleName &&
          grant.principal === newPrincipal.value &&
          grant.target === target,
      );

      const updatedGrants = grantExists
        ? prevGrants
        : [...prevGrants, newGrant];

      const newRoleChangesState = {
        grants: updatedGrants,
        revokes: updatedRevokes,
      };

      if (onRoleChanges) {
        onRoleChanges(newRoleChangesState);
      }

      const currentValue = methods.getValues(roleName) || [];
      if (
        !currentValue.some(
          (p: PrincipalOption) => p.value === newPrincipal.value,
        )
      ) {
        methods.setValue(roleName, [...currentValue, newPrincipal]);
      }
    },
    [
      data?.roleAssignmentTarget,
      methods,
      onRoleChanges,
      currentRoleChanges,
      isEditMode,
    ],
  );

  const handleRevokeAccess = useCallback(
    (roleName: string, principalValue: string) => {
      setError(null);

      if (isEditMode && !data?.roleAssignmentTarget) {
        setError("Cannot revoke roles: No valid target found");
        return;
      }

      const target = !isEditMode ? "" : data?.roleAssignmentTarget;

      const prevGrants = currentRoleChanges.grants;
      const prevRevokes = currentRoleChanges.revokes;

      const newRevoke: RoleAssignmentChange = {
        roleName,
        principal: principalValue,
        target: target,
      };

      const updatedGrants = prevGrants.filter(
        (grant) =>
          !(
            grant.roleName === roleName &&
            grant.principal === principalValue &&
            grant.target === target
          ),
      );

      const revokeExists = prevRevokes.some(
        (revoke) =>
          revoke.roleName === roleName &&
          revoke.principal === principalValue &&
          revoke.target === target,
      );

      const updatedRevokes = revokeExists
        ? prevRevokes
        : [...prevRevokes, newRevoke];

      const newRoleChangesState = {
        grants: updatedGrants,
        revokes: updatedRevokes,
      };

      if (onRoleChanges) {
        onRoleChanges(newRoleChangesState);
      }

      const currentValue = methods.getValues(roleName) || [];
      const updatedValue = currentValue.filter(
        (principal: PrincipalOption) => principal.value !== principalValue,
      );
      methods.setValue(roleName, updatedValue);
    },
    [
      data?.roleAssignmentTarget,
      methods,
      onRoleChanges,
      currentRoleChanges,
      isEditMode,
    ],
  );

  const isDisabled = isEditMode ? disabled : false;
  return (
    <div>
      {error ? (
        <Alert className="mb-md" severity="error">
          {error}
        </Alert>
      ) : null}

      <FormProvider {...methods}>
        <section data-testid="acl-form-fields">
          <div className="flex flex-col gap-xl">
            {rolesToDisplay.map((role) => (
              <div key={role.id}>
                <ACLPrincipalPicker
                  disabled={isDisabled}
                  fieldName={role.id}
                  label={role.displayName}
                  onGrantAccess={(newPrincipal: PrincipalOption) =>
                    handleGrantAccess(role.id, newPrincipal)
                  }
                  onRevokeAccess={(principalValue: string) =>
                    handleRevokeAccess(role.id, principalValue)
                  }
                  queryRef={queryData}
                  role={role.id}
                  target={target}
                />
              </div>
            ))}
          </div>
        </section>
      </FormProvider>
    </div>
  );
}
